import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, Button, Paper, Alert,
  MenuItem, CircularProgress, InputAdornment, IconButton, FormControlLabel, Checkbox,
} from '@mui/material';
import { Visibility, VisibilityOff, Phone, Email, Person, Work } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { colors, designTokens } from '../../theme/theme';
import { useTranslation } from 'react-i18next';

const INDUSTRIES = ['Tech & IT', 'Design & Creative', 'Marketing & SMM', 'Finance & Banking', 'Education', 'Healthcare', 'Legal', 'Startup / Entrepreneurship', 'Other'];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, user, isAuthenticated } = useAuth();
  const { t } = useTranslation();

  // If not authenticated, redirect to login with register mode
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?mode=register', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const [form, setForm] = useState({
    first_name: '', last_name: '', father_name: '',
    email: user?.email || '', phone: user?.phone || '',
    industry: '', password: '', confirm_password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Pre-fill from user context (email or phone from OTP)
  useEffect(() => {
    if (user) {
      setForm(f => ({
        ...f,
        email: user.email || f.email,
        phone: user.phone || f.phone,
      }));
    }
  }, [user]);

  const update = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }));

  const validatePassword = (pw: string): string | null => {
    if (pw.length < 8) return 'Password must be at least 8 characters';
    if (!/[a-z]/.test(pw)) return 'Password must contain a lowercase letter';
    if (!/[A-Z]/.test(pw)) return 'Password must contain an uppercase letter';
    if (!/[0-9]/.test(pw)) return 'Password must contain a number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pw)) return 'Password must contain a special character';
    return null;
  };

  const handleSubmit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First name and last name are required'); return;
    }
    if (!form.email || !form.email.includes('@')) {
      setError('A valid email address is required'); return;
    }
    if (!form.password) {
      setError('Please create a password'); return;
    }
    const pwError = validatePassword(form.password);
    if (pwError) { setError(pwError); return; }
    if (form.password !== form.confirm_password) {
      setError('Passwords do not match'); return;
    }

    setError('');
    setLoading(true);
    try {
      await register({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        father_name: form.father_name.trim() || undefined,
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        industry: form.industry || undefined,
        password: form.password,
      });
      navigate('/safety');
    } catch (e: any) {
      console.warn('[RegisterPage] register failed', e);
      setError(e.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colors.background.default, px: 2, py: 4 }}>
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: { xs: 3.5, sm: 4.5 }, borderRadius: 5, border: `1px solid ${designTokens.glass.border}`, bgcolor: designTokens.glass.surface, backdropFilter: designTokens.glass.blur }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h3" sx={{ mb: 1 }}>☕</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: colors.primary.main }}>
              {t('auth.complete_profile')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Tell us about yourself so your tablemates can get to know you.
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

          {/* Name fields */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField fullWidth label={`${t('auth.first_name')} *`} value={form.first_name}
              onChange={e => update('first_name', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: colors.neutral[400], fontSize: 20 }} /></InputAdornment> }}
            />
            <TextField fullWidth label={`${t('auth.last_name')} *`} value={form.last_name}
              onChange={e => update('last_name', e.target.value)} />
          </Box>

          <TextField fullWidth label="Father's Name" value={form.father_name}
            onChange={e => update('father_name', e.target.value)}
            helperText="Standard in Azerbaijan — helps us verify your identity"
            sx={{ mb: 2 }} />

          {/* Contact info */}
          <TextField fullWidth label={`${t('auth.email')} *`} type="email" value={form.email}
            onChange={e => update('email', e.target.value)}
            disabled={!!user?.email}
            InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: colors.neutral[400], fontSize: 20 }} /></InputAdornment> }}
            helperText="For receipts, notifications, and account recovery"
            sx={{ mb: 2 }} />

          <TextField fullWidth label={t('auth.phone')} value={form.phone}
            onChange={e => update('phone', e.target.value)}
            disabled={!!user?.phone}
            placeholder="+994 50 123 45 67"
            InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ color: colors.neutral[400], fontSize: 20 }} /></InputAdornment> }}
            helperText={user?.phone ? "Verified via OTP" : "Optional — for SMS notifications"}
            sx={{ mb: 2 }} />

          <TextField fullWidth select label={t('profile.industry')} value={form.industry}
            onChange={e => update('industry', e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Work sx={{ color: colors.neutral[400], fontSize: 20 }} /></InputAdornment> }}
            sx={{ mb: 3 }}>
            <MenuItem value="">— Select your industry —</MenuItem>
            {INDUSTRIES.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
          </TextField>

          {/* Password */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: colors.neutral[600] }}>Create a Password</Typography>
          <TextField fullWidth label="Password *" type={showPassword ? 'text' : 'password'}
            value={form.password} onChange={e => update('password', e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Min 8 chars, uppercase, lowercase, number, special character"
            sx={{ mb: 2 }} />

          <TextField fullWidth label="Confirm Password *" type={showPassword ? 'text' : 'password'}
            value={form.confirm_password} onChange={e => update('confirm_password', e.target.value)}
            error={form.confirm_password.length > 0 && form.password !== form.confirm_password}
            helperText={form.confirm_password.length > 0 && form.password !== form.confirm_password ? 'Passwords do not match' : ''}
            sx={{ mb: 3 }} />

          <FormControlLabel
            control={<Checkbox checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} />}
            label={
              <Typography variant="body2" sx={{ color: colors.neutral[600] }}>
                I agree to the{' '}
                <a href="/terms" target="_blank" style={{ color: colors.primary.main }}>Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" style={{ color: colors.primary.main }}>Privacy Policy</a>
              </Typography>
            }
            sx={{ mb: 2, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.5 } }}
          />

          <Button fullWidth variant="contained" size="large" onClick={handleSubmit}
            disabled={loading || !termsAccepted} sx={{ py: 1.5, fontWeight: 700, fontSize: '1rem' }}>
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Create My Account'}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
